import type {
  TenantExpiryAlert,
  TenantLifecycleAction,
  TenantOnboardingStepKey,
  TenantPlanStatus,
} from './api/tenants.graphql';

/** OS-1713 / OS-1720 — textos en español para estados y planes (la API guarda códigos en inglés). */
export const STATUS_LABELS: Record<string, string> = {
  pilot: 'Piloto',
  trial: 'Trial',
  active: 'Activo',
  past_due: 'En mora',
  suspended: 'Suspendido',
  cancelled: 'Cancelado',
};

/** Orden de los estados en filtros y KPIs. */
export const PLAN_STATUS_ORDER: readonly TenantPlanStatus[] = [
  'pilot',
  'active',
  'past_due',
  'suspended',
  'cancelled',
  'trial',
];

export const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function planLabel(tier: string): string {
  return PLAN_LABELS[tier] ?? tier;
}

interface LifecycleActionView {
  /** Texto del botón. */
  label: string;
  /** Título del diálogo de confirmación. */
  title: string;
  confirmLabel: string;
  danger: boolean;
  /** Suspender y cancelar cortan el acceso: el diálogo muestra cuántas cuentas lo pierden. */
  showImpact: boolean;
  description: string;
  reasonPlaceholder: string;
}

/**
 * OS-1720 — presentación de cada acción del ciclo de vida. Qué acciones existen para un tenant, a
 * qué estado lleva cada una y si piden motivo lo decide el API (`allowedLifecycleActions`); aquí
 * solo cómo se muestran.
 */
export const LIFECYCLE_ACTIONS: Record<TenantLifecycleAction, LifecycleActionView> = {
  CONVERT: {
    label: 'Convertir a pago',
    title: 'Convertir a cliente de pago',
    confirmLabel: 'Convertir a pago',
    danger: false,
    showImpact: false,
    description:
      'El tenant pasa a activo y se registra la fecha de conversión. Conserva usuarios, configuración y módulos. La facturación se configura aparte (perfil de facturación, pendiente).',
    reasonPlaceholder: 'Ej.: firmó el contrato anual',
  },
  SETTLE_PAST_DUE: {
    label: 'Pago al día',
    title: 'Marcar el pago al día',
    confirmLabel: 'Marcar al día',
    danger: false,
    showImpact: false,
    description: 'El tenant sale de mora y vuelve a activo. Siempre conservó el acceso.',
    reasonPlaceholder: 'Ej.: pagó las facturas pendientes',
  },
  RESUME: {
    label: 'Reactivar',
    title: 'Reactivar tenant',
    confirmLabel: 'Reactivar',
    danger: false,
    showImpact: false,
    description: 'Los usuarios del tenant recuperan el acceso de inmediato.',
    reasonPlaceholder: 'Ej.: se resolvió el motivo de la suspensión',
  },
  REACTIVATE: {
    label: 'Reactivar cancelado',
    title: 'Reactivar tenant cancelado',
    confirmLabel: 'Reactivar',
    danger: false,
    showImpact: false,
    description:
      'El tenant recupera el acceso con sus datos, usuarios y módulos. No genera cobros ni suscripciones.',
    reasonPlaceholder: 'Ej.: el cliente retoma el servicio',
  },
  MARK_PAST_DUE: {
    label: 'Marcar en mora',
    title: 'Marcar en mora',
    confirmLabel: 'Marcar en mora',
    danger: true,
    showImpact: false,
    description:
      'El tenant queda en mora pero conserva el acceso. Sirve para seguimiento comercial; no suspende ni cobra.',
    reasonPlaceholder: 'Ej.: factura de septiembre vencida hace 15 días',
  },
  SUSPEND: {
    label: 'Suspender',
    title: 'Suspender tenant',
    confirmLabel: 'Suspender',
    danger: true,
    showImpact: true,
    description:
      'Los usuarios pierden el acceso en menos de un minuto (si el enforcement está activo). Se puede reactivar después.',
    reasonPlaceholder: 'Ej.: mora de dos meses en el pago',
  },
  CANCEL: {
    label: 'Cancelar',
    title: 'Cancelar tenant',
    confirmLabel: 'Cancelar tenant',
    danger: true,
    showImpact: true,
    description:
      'Los usuarios pierden el acceso. Los datos se conservan; solo un administrador Super puede reactivarlo.',
    reasonPlaceholder: 'Ej.: el cliente terminó el contrato',
  },
};

/** Debe coincidir con `STATUS_REASON_MIN_LENGTH` / `MAX_LENGTH` del API (el API valida igual). */
export const STATUS_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** OS-1720 — texto de la alerta de vencimiento: "Vence en 3 días", "Vence hoy", "Vencido hace 2 días". */
export function expiryAlertLabel(alert: TenantExpiryAlert): string {
  if (alert.level === 'EXPIRED') {
    const days = Math.abs(alert.daysRemaining);
    return days === 0 ? 'Vencido hoy' : `Vencido hace ${plural(days, 'día', 'días')}`;
  }
  if (alert.daysRemaining === 0) {
    return 'Vence hoy';
  }
  if (alert.daysRemaining === 1) {
    return 'Vence mañana';
  }
  return `Vence en ${plural(alert.daysRemaining, 'día', 'días')}`;
}

/** Fecha corta en hora Colombia (las fechas del ciclo de vida se calculan en ese calendario). */
export function formatLifecycleDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CO', {
    dateStyle: 'medium',
    timeZone: 'America/Bogota',
  });
}

/** `YYYY-MM-DD` de hoy + `days` en Colombia, para los límites del selector de fecha. */
export function bogotaDateInput(days: number, now: Date = new Date()): string {
  const bogota = new Date(now.getTime() - 5 * 60 * 60 * 1000);
  bogota.setUTCDate(bogota.getUTCDate() + days);
  return bogota.toISOString().slice(0, 10);
}

/** Límites técnicos del API para el fin del piloto: desde mañana hasta 365 días. */
export const PILOT_END_RANGE = { minDays: 1, maxDays: 365 } as const;

/** Pestañas de la ficha del tenant (enlazables con `?tab=`). */
/**
 * Pestañas de la ficha (`?tab=`). OS-1724 — `summary` (Resumen 360) es la primera y la de entrada.
 * OS-1726 — `audit`: auditoría del tenant (con `platform.audit.read`).
 */
export const TENANT_TABS = ['summary', 'general', 'config', 'modules', 'billing', 'factus', 'audit'] as const;
export type TenantTab = (typeof TENANT_TABS)[number];

/** Id del panel del administrador inicial en la ficha (destino del paso de primer ingreso). */
export const ADMIN_INVITATION_PANEL_ID = 'tenant-admin-invitation';

interface OnboardingStepView {
  label: string;
  hint: string;
  /** Dónde se completa desde la consola; `null` si lo hace el restaurante en su app. */
  target: { tab: TenantTab } | { anchor: string } | null;
}

/** OS-1718 — textos y destino de cada paso del checklist de activación. */
export const ONBOARDING_STEPS: Record<TenantOnboardingStepKey, OnboardingStepView> = {
  COMPANY_DATA: {
    label: 'Datos de la empresa',
    hint: 'Razón social, NIT y email.',
    target: { tab: 'general' },
  },
  ACTIVE_LOCATION: {
    label: 'Sede activa',
    hint: 'Al menos una sede activa. Se gestiona desde la app del restaurante.',
    target: null,
  },
  BRANDING: {
    label: 'Branding',
    hint: 'Logo o colores propios.',
    target: { tab: 'config' },
  },
  MODULES: {
    label: 'Módulos contratados',
    hint: 'Al menos un módulo además de los que vienen siempre incluidos.',
    target: { tab: 'modules' },
  },
  ADMIN_LOGIN: {
    label: 'Primer ingreso del administrador',
    hint: 'El administrador del tenant inició sesión al menos una vez.',
    target: { anchor: ADMIN_INVITATION_PANEL_ID },
  },
  FIRST_USER: {
    label: 'Primer empleado o usuario',
    hint: 'Algún usuario además del administrador inicial. Se crea desde la app del restaurante.',
    target: null,
  },
  FIRST_USAGE: {
    label: 'Primer uso de un módulo',
    hint: 'Una orden, un check-in o una exportación PILA. Se actualiza una vez al día.',
    target: null,
  },
};

/** OS-1718 — opciones del filtro de onboarding del listado. */
export const ONBOARDING_FILTER_OPTIONS = [
  { value: 'INCOMPLETE', label: 'Onboarding incompleto' },
  { value: 'STALLED', label: 'Onboarding estancado' },
] as const;

/** OS-1722 — catálogos del perfil de facturación (mismos códigos que valida el API). */
export const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'annual', label: 'Anual' },
] as const;

export const PAYMENT_METHOD_OPTIONS = [
  { value: 'transfer', label: 'Transferencia bancaria' },
  { value: 'pse', label: 'PSE' },
  { value: 'card_external', label: 'Tarjeta (en pasarela externa)' },
  { value: 'invoice_30', label: 'Factura a 30 días' },
] as const;

/** Mismos códigos y textos que el web-app muestra en "Empresa". */
export const TAX_REGIME_OPTIONS = [
  { value: 'RESPONSABLE_IVA', label: 'Responsable de IVA' },
  { value: 'NO_RESPONSABLE_IVA', label: 'No responsable de IVA' },
  { value: 'REGIMEN_SIMPLE', label: 'Régimen simple' },
] as const;

export const BILLING_REQUIREMENT_LABELS: Record<string, string> = {
  legalName: 'razón social',
  nit: 'NIT con dígito de verificación',
  billingEmail: 'email de facturación',
};

export function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string | null,
): string {
  if (!value) {return '—';}
  return options.find((option) => option.value === value)?.label ?? value;
}
